import {
	Box,
	Divider,
	FormControl,
	Grid,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	Typography,
} from '@mui/material';
import { endOfWeek, startOfToday, startOfWeek } from 'date-fns';
import { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { useEffect, useMemo, useState } from 'react';
import { DayString, UserProps } from '../../constants/Common';
import { SiteLog } from '../../constants/SiteLogs';
import firebaseApi from '../../firebase/firebaseApi';
import { DateDataTable } from '../DataTable/DateDataTable';
import { ExpandDialog } from '../ExpandDialog/ExpandDialog';
import {
	formatDayOfWeek,
	formatSlashedDate,
	formatTime,
} from '../helpers/dateFormatters';
import { getDayNumber, toZeroedMonday } from '../helpers/dateUtilities';
import { LoadableImage } from '../Image/LoadableImage';
import { LoadingDots } from '../Management/subcomponents/LoadingDots';
import {
	RowData,
	SiteLogTableData,
	signInBookCustomSearch,
} from './SignInOutLogHelpers';

type SiteLogRecord = SiteLog & {
	userType: string;
};

const toSiteLogRecord = (siteLogs: SiteLog[]): Record<string, SiteLogRecord> =>
	Object.fromEntries(
		siteLogs.map((log) => [
			log.id,
			{
				userType: log.isGuest === true ? 'Guest' : 'User',
				...log,
			} as SiteLogRecord,
		]),
	);

/** Converts "Mon 1/1/1970" to "Monday"
 *
 * Also accepts arrays, and will output as an array.
 * Must be a function to accept overloads
 */
function dateStringToDay(dates: string[]): DayString[];
function dateStringToDay(dates: string): DayString;
function dateStringToDay(dates: string | string[]): DayString | DayString[] {
	const mapDate = (date: string): DayString =>
		date.split(' ')[0] as DayString;
	return typeof dates === 'string' ? mapDate(dates) : dates.map(mapDate);
}

export const SignInOutLog = ({ userDetails }: UserProps): JSX.Element => {
	const [tableData, setTableData] = useState<SiteLogTableData[]>([]);
	const [selected, setSelected] = useState<number>(0);
	const [tableLoading, setTableLoading] = useState<boolean>(true);
	const [startDate, setStartDate] = useState<Date>(
		startOfWeek(startOfToday()),
	);
	const [companySiteLogList, setCompanySiteLogList] = useState<SiteLog[]>([]);
	const [contractedToSiteLogList, setContractedToSiteLogList] = useState<
		SiteLog[]
	>([]);
	const [currentSiteLogRecord, setCurrentSiteLogRecord] = useState<
		Record<string, SiteLogRecord>
	>({});

	const tableOptions: MUIDataTableOptions = {
		filter: true,
		tableBodyHeight: 'calc(100vh - 304px)',
		textLabels: {
			body: {
				noMatch: tableLoading ? (
					<LoadingDots />
				) : (
					'Sorry, no matching sign in or out logs found'
				),
			},
		},
		customSearch: (searchString, currentRow) =>
			signInBookCustomSearch(searchString, currentRow as RowData),
	};

	const checkAccountType = userDetails.accountType === 'management';
	const title = checkAccountType
		? 'Site Sign In Book'
		: 'Company Sign In Book';

	useEffect(() => {
		setTableLoading(true);
		setTableData([]);
		const endDate = endOfWeek(startDate);
		let inSub: () => void = () => {
			return;
		};

		// Queries given by https://tradelegion.atlassian.net/l/c/P6GiN5qy

		// Main contractor management
		if (
			checkAccountType &&
			userDetails.siteCompanyID === userDetails.companyID
		) {
			inSub = firebaseApi.siteLogsBySiteDateRangeSubscription(
				userDetails.siteID,
				startDate,
				endDate,
				(siteLogs) => setCompanySiteLogList(Object.values(siteLogs)),
			);
		}
		// Subcontractor management
		else if (
			checkAccountType &&
			userDetails.siteCompanyID !== userDetails.companyID
		) {
			inSub = firebaseApi.siteLogsBySiteContractedToDateRangeSubscription(
				userDetails.siteID,
				userDetails.companyID,
				startDate,
				endDate,
				setContractedToSiteLogList,
			);
		}
		// Senior management contractor
		else if (userDetails.accountType === 'seniorManagement') {
			// First subscription pulls all logs from sites belonging to the users company
			inSub = firebaseApi.siteLogsBySiteCompanyDateRangeSubscription(
				userDetails.companyID,
				startDate,
				endDate,
				(siteLogs) => setCompanySiteLogList(Object.values(siteLogs)),
			);
			// Second subscription pulls all site logs where the log is contracted to the users company
			// but not on a site the users company owns
			const inSub2 =
				firebaseApi.siteLogsByContractedToDateRangeSubscription(
					userDetails.companyID,
					startDate,
					endDate,
					(siteLogs) => {
						const newSiteLogs = Object.values(siteLogs).filter(
							(siteLog) =>
								siteLog.site.companyID !==
								userDetails.companyID,
						);
						setContractedToSiteLogList(newSiteLogs);
					},
				);

			return () => {
				inSub();
				inSub2();
			};
		} else if (userDetails.accountType === 'handler') {
			inSub = firebaseApi.siteLogsByCompanyDateRangeSubscription(
				userDetails.companyID,
				startDate,
				endDate,
				(siteLogs) => setCompanySiteLogList(Object.values(siteLogs)),
			);
		}
		return () => {
			inSub();
		};
	}, [startDate, userDetails, checkAccountType]);

	useEffect(() => {
		setCurrentSiteLogRecord(
			toSiteLogRecord([
				...companySiteLogList,
				...contractedToSiteLogList,
			]),
		);
	}, [companySiteLogList, contractedToSiteLogList]);

	useEffect(() => {
		if (Object.keys(currentSiteLogRecord).length !== 0) {
			const data = Object.values(
				currentSiteLogRecord,
			).map<SiteLogTableData>((entry) => ({
				worker: entry.worker,
				company: entry.company.name,
				site: entry.site.name,
				userType: entry.userType,
				date: formatSlashedDate(
					entry.out
						? entry.out.dateTime.toDate()
						: entry.in.dateTime.toDate(),
				),
				in: `${formatDayOfWeek(
					entry.in.dateTime.toDate(),
				)} ${formatTime(entry.in.dateTime.toDate())}`,
				out: entry.out
					? `${formatDayOfWeek(
							entry.out.dateTime.toDate(),
					  )} ${formatTime(entry.out.dateTime.toDate())}`
					: '',
			}));
			setTableData(data);
		}
		setTableLoading(false);
	}, [currentSiteLogRecord]);

	const cellWidth = '14.5%';

	const columns: MUIDataTableColumnDef[] = [
		{
			name: 'worker',
			label: 'Name',
			options: {
				setCellHeaderProps: () => ({
					style: { width: cellWidth },
				}),
				customBodyRender: (value): JSX.Element => {
					const worker = value as SiteLog['worker'];
					return (
						<>
							<Typography variant="body2">
								{worker.name}
							</Typography>
							<Typography variant="caption">
								{worker.mobileNumber}
							</Typography>
						</>
					);
				},
			},
		},
		{
			name: 'company',
			label: 'Company',
			options: {
				setCellHeaderProps: () => ({
					style: { width: cellWidth },
				}),
			},
		},
		{
			name: 'site',
			label: 'Site',
			options: {
				setCellHeaderProps: () => ({
					style: { width: cellWidth },
				}),
			},
		},
		{
			name: 'userType',
			label: 'Type',
			options: {
				setCellHeaderProps: () => ({
					style: { width: cellWidth },
				}),
			},
		},
		{
			name: 'date',
			label: 'Date',
			options: {
				setCellHeaderProps: () => ({
					style: { width: cellWidth },
				}),
				filterType: 'custom',
				filterOptions: {
					logic: (day, filters) =>
						filters.length && filters[0] !== ''
							? !filters.includes(dateStringToDay(day))
							: false,
					display: (
						filterList,
						onChange,
						index,
						column,
						filterData,
					): JSX.Element => {
						return (
							<FormControl>
								{/* this refuses to line up and I have no idea why */}
								<InputLabel shrink sx={{ ml: -2 }}>
									Day
								</InputLabel>
								<Select
									notched
									variant="standard"
									displayEmpty
									renderValue={(value): string =>
										value || 'All'
									}
									// should only ever be one value atm
									value={
										filterList[index].length === 0
											? ''
											: filterList[index][0]
									}
									onChange={(event): void => {
										filterList[index] = [
											event.target.value,
										];
										onChange(
											filterList[index],
											index,
											column,
										);
									}}>
									<MenuItem key="All" value="">
										<ListItemText primary="All" />
									</MenuItem>
									{filterData[index]
										.map(dateStringToDay)
										.sort(
											(dayA, dayB) =>
												toZeroedMonday(
													getDayNumber(dayA),
												) -
												toZeroedMonday(
													getDayNumber(dayB),
												),
										)
										.map((day) => (
											<MenuItem key={day} value={day}>
												<ListItemText primary={day} />
											</MenuItem>
										))}
								</Select>
							</FormControl>
						);
					},
				},
				customFilterListOptions: {
					render: (value) => (value[0] === '' ? [] : value),
				},
			},
		},
		{
			name: 'in',
			label: 'Sign In',
			options: {
				setCellHeaderProps: () => ({
					style: { width: cellWidth },
				}),
			},
		},
		{
			name: 'out',
			label: 'Sign Out',
			options: {
				setCellHeaderProps: () => ({
					style: { width: cellWidth },
				}),
			},
		},
	];

	const selectedEntry = useMemo(
		() => Object.values(currentSiteLogRecord)[selected],
		[selected, currentSiteLogRecord],
	);

	return (
		<Stack
			direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'row' }}
			divider={
				<Divider
					sx={{ ml: 1, mr: 1 }}
					orientation="vertical"
					flexItem
				/>
			}
			spacing={2}>
			<Box sx={{ minWidth: '75%' }}>
				<DateDataTable
					title={title}
					tableData={tableData}
					columns={columns}
					date={[startDate, setStartDate]}
					selection={[selected, setSelected]}
					customTableOptions={tableOptions}
				/>
			</Box>
			<Box sx={{ minWidth: '25%' }}>
				<Grid
					spacing={1}
					container
					alignItems="center"
					flexDirection={{
						xs: 'column',
						sm: 'row',
						md: 'row',
						lg: 'column',
					}}>
					<Grid item lg={12} md={6} sm={6} xs={12}>
						{selectedEntry ? (
							<Stack alignItems="center" spacing={1}>
								<Typography variant="h5">Sign In</Typography>
								<ExpandDialog
									buttonStyles={{
										width: '100%',
										height: '100%',
										paddingX: 0.5,
									}}>
									<LoadableImage
										src={selectedEntry.in.photoUrl}
										srcLoading={false}
										altText="No Sign Out Image Data"
										imageStyle={{
											width: '100%',
											height: '100%',
											minHeight: '200px',
											maxWidth: '72%',
										}}
									/>
								</ExpandDialog>
							</Stack>
						) : (
							<Typography variant="subtitle2">
								No Image
							</Typography>
						)}
					</Grid>
					<Grid item lg={12} md={6} sm={6} xs={12}>
						{selectedEntry?.out ? (
							<Stack alignItems="center" spacing={1}>
								<Typography variant="h5">Sign Out</Typography>
								<ExpandDialog
									buttonStyles={{
										width: '100%',
										height: '100%',
										paddingX: 0.5,
									}}>
									<LoadableImage
										src={selectedEntry.out.photoUrl}
										srcLoading={false}
										altText="No Sign Out Image Data"
										imageStyle={{
											width: '100%',
											height: '100%',
											minHeight: '200px',
											maxWidth: '72%',
										}}
									/>
								</ExpandDialog>
							</Stack>
						) : (
							<Typography variant="subtitle2">
								No Image
							</Typography>
						)}
					</Grid>
				</Grid>
			</Box>
		</Stack>
	);
};
